import {
  Activity,
  BellRing,
  ChartColumn,
  Clock,
  Eye,
  Globe,
  Headphones,
  Smartphone,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Empty } from '../components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import {
  Tabs,
  TabsContent,
  UnderlineTabsList,
  UnderlineTabsTrigger,
} from '../components/ui/tabs';
import {
  type AnalyticsOverview,
  type ListenerHistory,
  type LiveViewerHistory,
  type PlayTimeHistory,
  type PushCtrItem,
  type TopPostItem,
  useAnalyticsOverview,
  useDeviceBreakdown,
  useGeoReach,
  useListenerHistory,
  useLiveViewerHistory,
  usePlayTimeHistory,
  usePostViewHistory,
  usePushCtr,
  usePushSendTimeCtr,
  useSessionHistory,
  useSessionsHeatmap,
  useTopPosts,
} from '../lib/api/analytics';
import { DeviceBreakdown } from '../components/analytics/DeviceBreakdown';
import { GeoHeatmap } from '../components/analytics/GeoHeatmap';
import { PushSendTimeCtr } from '../components/analytics/PushSendTimeCtr';
import { SessionsHourHeatmap } from '../components/analytics/SessionsHourHeatmap';
import { TopCountriesTable } from '../components/analytics/TopCountriesTable';
import { useChannels } from '../lib/api/channels';
import type { AnalyticsRangeKey } from '../lib/api/keys';
import { usePublicProgrammes } from '../lib/api/programmes';
import { useLocaleStore } from '../lib/locales';
import { fmtDateTime } from '../lib/date';

/**
 * Chart colours pulled from the admin design tokens. Recharts wants concrete
 * stroke values, not Tailwind class names — and the codebase doesn't expose
 * `--brand` / `--line` etc. as CSS custom properties at runtime, so referencing
 * them via `var(--brand)` would render as transparent. Sourced from
 * `apps/admin/design.json` to stay aligned with the rest of the app.
 */
const CHART_COLORS = {
  brand: '#313380',
  brandActive: '#1d1e4d',
  secondary: '#008ce1',
  line: '#eaeaea',
  inkSubtle: '#888888',
  surface: '#ffffff',
  border: '#eaeaea',
} as const;

const RANGES: { key: AnalyticsRangeKey; label: string }[] = [
  { key: '1h', label: 'Last hour' },
  { key: '24h', label: 'Last 24h' },
  { key: '7d', label: 'Last 7d' },
  { key: '30d', label: 'Last 30d' },
];

export default function Analytics(): JSX.Element {
  const [range, setRange] = useState<AnalyticsRangeKey>('24h');
  const overview = useAnalyticsOverview();

  return (
    <>
      <Topbar title="Analytics" subtitle="Listener counts, live viewers, episode views." />
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <ChartColumn className="h-5 w-5 text-brand" />
            Analytics
          </span>
        }
        subtitle="Server time-series + client events · auto-refresh"
        actions={<RangePicker value={range} onChange={setRange} />}
      />

      <div className="grid gap-4 px-5 pb-8">
        <OverviewStrip overview={overview.data} loading={overview.isLoading} />

        {/* Tab bar sits in its own card-style container with no padding,
         *  so the underline divider runs flush with the card edges. The
         *  active-tab underline visually breaks the divider — the canonical
         *  dashboard-nav cue. */}
        <Tabs defaultValue="audience" className="space-y-4">
          <div className="-mx-1 overflow-x-auto px-1">
            <UnderlineTabsList className="px-1">
              <UnderlineTabsTrigger value="audience">
                <Headphones className="h-3.5 w-3.5" /> Audience
              </UnderlineTabsTrigger>
              <UnderlineTabsTrigger value="content">
                <Eye className="h-3.5 w-3.5" /> Content
              </UnderlineTabsTrigger>
              <UnderlineTabsTrigger value="engagement">
                <BellRing className="h-3.5 w-3.5" /> Engagement
              </UnderlineTabsTrigger>
              <UnderlineTabsTrigger value="reach">
                <Globe className="h-3.5 w-3.5" /> Reach
              </UnderlineTabsTrigger>
            </UnderlineTabsList>
          </div>

          <TabsContent value="audience" className="mt-0 grid gap-4 lg:grid-cols-2">
            <ChannelListenersPanel range={range} />
            <ProgrammeLiveViewersPanel range={range} />
            <div className="lg:col-span-2">
              <SessionsHeatmapPanel />
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-0 grid gap-4">
            <PostViewsPanel range={range} />
            <SessionsPanel range={range} />
          </TabsContent>

          <TabsContent value="engagement" className="mt-0 grid gap-4 lg:grid-cols-2">
            <PushSendTimePanel />
            <PlayTimePanel range={range} />
            <div className="lg:col-span-2">
              <PushCtrPanel range={range} />
            </div>
          </TabsContent>

          <TabsContent value="reach" className="mt-0 grid gap-4">
            <ReachPanel />
            <DeviceBreakdownPanel />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

// ---------- Range picker ----------

function RangePicker({
  value,
  onChange,
}: {
  value: AnalyticsRangeKey;
  onChange: (v: AnalyticsRangeKey) => void;
}): JSX.Element {
  return (
    <div className="flex gap-1 rounded-md border border-line bg-surface p-1">
      {RANGES.map((r) => (
        <Button
          key={r.key}
          variant={value === r.key ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => onChange(r.key)}
        >
          {r.label}
        </Button>
      ))}
    </div>
  );
}

// ---------- Overview strip ----------

function OverviewStrip({
  overview,
  loading,
}: {
  overview: AnalyticsOverview | undefined;
  loading: boolean;
}): JSX.Element {
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-3">
          <Spinner label="Loading overview…" />
        </CardContent>
      </Card>
    );
  }
  const totalListeners = (overview?.latestListeners ?? [])
    .map((c) => c.listeners ?? 0)
    .reduce((a, b) => a + b, 0);
  const liveCount = overview?.liveNow.length ?? 0;
  const liveViewers = (overview?.liveNow ?? [])
    .map((l) => l.viewers ?? 0)
    .reduce((a, b) => a + b, 0);
  const streamingCount = (overview?.latestListeners ?? []).filter((c) => c.streaming).length;
  const channelsCount = overview?.latestListeners.length ?? 0;

  return (
    <Card>
      <CardContent className="grid grid-cols-3 divide-x divide-line-hair px-0 py-0">
        <Stat
          icon={<Headphones className="h-4 w-4" />}
          label="Listeners now"
          value={totalListeners.toLocaleString()}
          sub={`${streamingCount} streaming`}
        />
        <Stat
          icon={<Users className="h-4 w-4" />}
          label="Watching now"
          value={liveViewers.toLocaleString()}
          sub={`${liveCount} live`}
        />
        <Stat
          icon={<Activity className="h-4 w-4" />}
          label="Channels"
          value={String(channelsCount)}
          sub="60 s sample"
        />
      </CardContent>
    </Card>
  );
}

function Stat({
  icon,
  label,
  value,
  sub,
}: {
  icon: JSX.Element;
  label: string;
  value: string;
  sub?: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="rounded-md border border-line bg-surface-muted p-1.5 text-brand">{icon}</div>
      <div className="min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">{label}</p>
        <p className="text-xl font-semibold tabular-nums leading-tight text-ink">{value}</p>
        {sub ? <p className="text-[10.5px] text-ink-muted">{sub}</p> : null}
      </div>
    </div>
  );
}

// ---------- Channel listeners ----------

function ChannelListenersPanel({ range }: { range: AnalyticsRangeKey }): JSX.Element {
  const channels = useChannels();
  const items = channels.data?.items ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const slug = selected ?? items[0]?.slug ?? null;
  const history = useListenerHistory(slug, range);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Radio listeners</CardTitle>
            <CardDescription>
              Shoutcast / Icecast listener count, sampled every minute. Gaps in the line = stream
              offline at that tick.
            </CardDescription>
          </div>
          {items.length > 1 ? (
            <Select value={slug ?? ''} onValueChange={(v) => setSelected(v)}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Channel" />
              </SelectTrigger>
              <SelectContent>
                {items.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {!slug ? (
          <Empty title="No channels" description="Add a radio channel first." />
        ) : history.isLoading ? (
          <Spinner label="Loading history…" />
        ) : history.data && history.data.points.length > 0 ? (
          <ListenerChart history={history.data} />
        ) : (
          <Empty
            title="No samples in this range"
            description="The sampler runs once a minute. Wait a few minutes after the channel goes live for points to appear."
          />
        )}
      </CardContent>
    </Card>
  );
}

function ListenerChart({ history }: { history: ListenerHistory }): JSX.Element {
  const data = history.points.map((p) => ({
    t: new Date(p.t).getTime(),
    avg: p.avg ?? 0,
    max: p.max ?? 0,
  }));
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
          <CartesianGrid stroke={CHART_COLORS.line} strokeOpacity={0.5} strokeDasharray="2 4" />
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            scale="time"
            tickFormatter={(v) => xAxisFmt(v, history.bucket)}
            stroke={CHART_COLORS.inkSubtle}
            fontSize={11}
          />
          <YAxis
            stroke={CHART_COLORS.inkSubtle}
            fontSize={11}
            allowDecimals={false}
            tickFormatter={(v) => Number(v).toLocaleString()}
          />
          <Tooltip
            formatter={(v: unknown) => Number(v).toLocaleString()}
            labelFormatter={(v) => fmtDateTime(new Date(v).toISOString())}
            contentStyle={{
              background: CHART_COLORS.surface,
              border: `1px solid ${CHART_COLORS.border}`,
              fontSize: 12,
            }}
          />
          <Line
            type="monotone"
            dataKey="avg"
            stroke={CHART_COLORS.brand}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            name="Average"
          />
          <Line
            type="monotone"
            dataKey="max"
            stroke={CHART_COLORS.brandActive}
            strokeWidth={1}
            strokeDasharray="3 3"
            dot={false}
            isAnimationActive={false}
            name="Peak in bucket"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Programme live viewers ----------

function ProgrammeLiveViewersPanel({ range }: { range: AnalyticsRangeKey }): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const programmes = usePublicProgrammes({ locale, page: 1, pageSize: 50 });
  // Only programmes with a YouTube source ever get live samples.
  const items = (programmes.data?.items ?? []).filter(
    (p) => p.youtubeChannelId || p.youtubeHandle || p.youtubeLiveStreamId,
  );
  const [selected, setSelected] = useState<string | null>(null);
  const slug = selected ?? items[0]?.slug ?? null;
  const history = useLiveViewerHistory(slug, range);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>YouTube live viewers</CardTitle>
            <CardDescription>
              `concurrentViewers` while the programme is live. No samples = the programme wasn't
              live at any point in the selected range.
            </CardDescription>
          </div>
          {items.length > 0 ? (
            <Select value={slug ?? ''} onValueChange={(v) => setSelected(v)}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Programme" />
              </SelectTrigger>
              <SelectContent>
                {items.map((p) => (
                  <SelectItem key={p.slug} value={p.slug}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <Empty
            title="No YouTube-enabled programmes"
            description="Add a YouTube channel id or handle to a programme to track its live viewers."
          />
        ) : !slug ? null : history.isLoading ? (
          <Spinner label="Loading history…" />
        ) : history.data && history.data.points.length > 0 ? (
          <LiveViewerChart history={history.data} />
        ) : (
          <Empty
            title="No live samples in this range"
            description="Either no broadcast happened in this window, or `youtubeLiveStreamId` isn't pinned (channel-search live discovery is intentionally disabled to protect quota)."
          />
        )}
      </CardContent>
    </Card>
  );
}

function LiveViewerChart({ history }: { history: LiveViewerHistory }): JSX.Element {
  const data = history.points.map((p) => ({
    t: new Date(p.t).getTime(),
    avg: p.avg ?? 0,
    max: p.max ?? 0,
  }));
  const peak = Math.max(0, ...data.map((d) => d.max));
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-ink-muted">
        <Badge variant="outline">Peak in range: {peak.toLocaleString()}</Badge>
      </div>
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <CartesianGrid stroke={CHART_COLORS.line} strokeOpacity={0.5} strokeDasharray="2 4" />
            <XAxis
              dataKey="t"
              type="number"
              domain={['dataMin', 'dataMax']}
              scale="time"
              tickFormatter={(v) => xAxisFmt(v, history.bucket)}
              stroke={CHART_COLORS.inkSubtle}
              fontSize={11}
            />
            <YAxis
              stroke={CHART_COLORS.inkSubtle}
              fontSize={11}
              allowDecimals={false}
              tickFormatter={(v) => Number(v).toLocaleString()}
            />
            <Tooltip
              formatter={(v: unknown) => Number(v).toLocaleString()}
              labelFormatter={(v) => fmtDateTime(new Date(v).toISOString())}
              contentStyle={{
                background: CHART_COLORS.surface,
                border: `1px solid ${CHART_COLORS.border}`,
                fontSize: 12,
              }}
            />
            <Line
              type="monotone"
              dataKey="avg"
              stroke={CHART_COLORS.brand}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              name="Average viewers"
            />
            <Line
              type="monotone"
              dataKey="max"
              stroke={CHART_COLORS.brandActive}
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
              isAnimationActive={false}
              name="Peak"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------- Phase 3: Post views ----------

function PostViewsPanel({ range }: { range: AnalyticsRangeKey }): JSX.Element {
  const history = usePostViewHistory(range);
  const top = useTopPosts(range, 10);
  const data = (history.data?.points ?? []).map((p) => ({
    t: new Date(p.t).getTime(),
    views: p.count,
    uniques: p.uniques,
  }));
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="inline-flex items-center gap-2">
              <Eye className="h-4 w-4 text-brand" /> Post views
            </CardTitle>
            <CardDescription>
              Per-bucket views and unique devices. Reported by mobile clients on each post-detail
              open; dwell-time is aggregated in the Top posts list.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {history.isLoading ? (
          <Spinner label="Loading…" />
        ) : data.length === 0 ? (
          <Empty
            title="No views in this range"
            description="The mobile clients haven't reported any `post_view` events in the selected window. Phase 3b wires the mobile sender — until that's deployed this stays empty."
          />
        ) : (
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }} barCategoryGap="20%">
                <CartesianGrid stroke={CHART_COLORS.line} strokeOpacity={0.5} strokeDasharray="2 4" />
                {/* Category axis (not numeric/time) — bars are inherently categorical
                    and a numeric axis anchors the leftmost bar onto the y-axis line.
                    `padding` inset keeps the first/last bars off the axes. */}
                <XAxis
                  dataKey="t"
                  type="category"
                  tickFormatter={(v) => xAxisFmt(Number(v), history.data!.bucket)}
                  stroke={CHART_COLORS.inkSubtle}
                  fontSize={11}
                  padding={{ left: 16, right: 16 }}
                />
                <YAxis
                  stroke={CHART_COLORS.inkSubtle}
                  fontSize={11}
                  allowDecimals={false}
                  tickFormatter={(v) => Number(v).toLocaleString()}
                />
                <Tooltip
                  formatter={(v: unknown) => Number(v).toLocaleString()}
                  labelFormatter={(v) => fmtDateTime(new Date(v).toISOString())}
                  contentStyle={{
                    background: CHART_COLORS.surface,
                    border: `1px solid ${CHART_COLORS.border}`,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="views" name="Views" fill={CHART_COLORS.brand} maxBarSize={40} />
                <Bar dataKey="uniques" name="Unique devices" fill={CHART_COLORS.secondary} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {top.data && top.data.items.length > 0 ? (
          <div>
            <h3 className="mb-2 text-sm font-semibold text-ink">Top posts in range</h3>
            <table className="table-base">
              <thead>
                <tr>
                  <th>Post</th>
                  <th className="w-24 text-right">Views</th>
                  <th className="w-24 text-right">Uniques</th>
                  <th className="w-28 text-right">Avg dwell</th>
                </tr>
              </thead>
              <tbody>
                {top.data.items.map((p) => (
                  <TopPostRow key={p.slug} item={p} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function TopPostRow({ item }: { item: TopPostItem }): JSX.Element {
  return (
    <tr>
      <td>
        <Link to={`/posts/${encodeURIComponent(item.slug)}`} className="text-brand hover:underline">
          {item.slug}
        </Link>
      </td>
      <td className="text-right tabular-nums">{item.views.toLocaleString()}</td>
      <td className="text-right tabular-nums">{item.uniques.toLocaleString()}</td>
      <td className="text-right tabular-nums text-ink-muted">
        {item.avgDwellSeconds !== null ? formatDwell(item.avgDwellSeconds) : '—'}
      </td>
    </tr>
  );
}

// ---------- Phase 3: Mobile sessions / DAU ----------

function SessionsPanel({ range }: { range: AnalyticsRangeKey }): JSX.Element {
  const history = useSessionHistory(range);
  const points = history.data?.points ?? [];
  const data = points.map((p) => ({
    t: new Date(p.t).getTime(),
    sessions: p.count,
    uniques: p.uniques,
  }));
  // For the 7d/30d ranges, "uniques per day" ≈ DAU. For shorter ranges it's
  // closer to a real-time-of-day pattern.
  const totalSessions = points.reduce((s, p) => s + p.count, 0);
  const peakUniques = points.reduce((m, p) => Math.max(m, p.uniques), 0);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="inline-flex items-center gap-2">
              <Activity className="h-4 w-4 text-brand" /> Mobile sessions
            </CardTitle>
            <CardDescription>
              One row per app foreground. Unique-devices ≈ DAU on the day bucket; peak in range:{' '}
              {peakUniques.toLocaleString()} devices · total: {totalSessions.toLocaleString()}{' '}
              session(s).
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {history.isLoading ? (
          <Spinner label="Loading…" />
        ) : data.length === 0 ? (
          <Empty
            title="No sessions in this range"
            description="Mobile clients haven't reported any `session_start` events yet."
          />
        ) : (
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid stroke={CHART_COLORS.line} strokeOpacity={0.5} strokeDasharray="2 4" />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  scale="time"
                  tickFormatter={(v) => xAxisFmt(v, history.data!.bucket)}
                  stroke={CHART_COLORS.inkSubtle}
                  fontSize={11}
                />
                <YAxis
                  stroke={CHART_COLORS.inkSubtle}
                  fontSize={11}
                  allowDecimals={false}
                  tickFormatter={(v) => Number(v).toLocaleString()}
                />
                <Tooltip
                  formatter={(v: unknown) => Number(v).toLocaleString()}
                  labelFormatter={(v) => fmtDateTime(new Date(v).toISOString())}
                  contentStyle={{
                    background: CHART_COLORS.surface,
                    border: `1px solid ${CHART_COLORS.border}`,
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="uniques"
                  stroke={CHART_COLORS.brand}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  name="Unique devices"
                />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  stroke={CHART_COLORS.secondary}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  dot={false}
                  isAnimationActive={false}
                  name="Sessions"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Phase 3: Push CTR ----------

function PushCtrPanel({ range }: { range: AnalyticsRangeKey }): JSX.Element {
  const ctr = usePushCtr(range);
  const items = ctr.data?.items ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <BellRing className="h-4 w-4 text-brand" /> Push notifications — click-through
        </CardTitle>
        <CardDescription>
          One row per delivered push in range. CTR = unique taps / delivered count. Pushes with
          delivered = 0 are drafts that never sent.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {ctr.isLoading ? (
          <Spinner label="Loading…" />
        ) : items.length === 0 ? (
          <Empty
            title="No pushes delivered in this range"
            description="Send a push from the Breaking news page; rows will appear here once delivered."
          />
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>Push</th>
                <th className="w-20">Locale</th>
                <th className="w-40">Delivered</th>
                <th className="w-24 text-right">Sent</th>
                <th className="w-24 text-right">Taps</th>
                <th className="w-24 text-right">Unique</th>
                <th className="w-24 text-right">CTR</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <PushCtrRow key={p.pushId} item={p} />
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function PushCtrRow({ item }: { item: PushCtrItem }): JSX.Element {
  return (
    <tr>
      <td className="font-medium text-ink">{item.title ?? `Push ${item.pushId}`}</td>
      <td>
        {item.locale ? (
          <Badge variant="outline" className="uppercase">
            {item.locale}
          </Badge>
        ) : (
          <span className="text-ink-muted">—</span>
        )}
      </td>
      <td className="text-sm text-ink-muted">
        {item.deliveredAt ? fmtDateTime(item.deliveredAt) : '—'}
      </td>
      <td className="text-right tabular-nums">{item.delivered.toLocaleString()}</td>
      <td className="text-right tabular-nums">{item.taps.toLocaleString()}</td>
      <td className="text-right tabular-nums">{item.uniqueTaps.toLocaleString()}</td>
      <td className="text-right tabular-nums">
        {item.ctr !== null ? `${(item.ctr * 100).toFixed(1)}%` : '—'}
      </td>
    </tr>
  );
}

// ---------- Phase 3: Audio play-time ----------

function PlayTimePanel({ range }: { range: AnalyticsRangeKey }): JSX.Element {
  const [scopeType, setScopeType] = useState<'all' | 'channel' | 'episode' | 'youtube'>('all');
  const history = usePlayTimeHistory(range, {
    scopeType: scopeType === 'all' ? undefined : scopeType,
  });
  const points = history.data?.points ?? [];
  const data = points.map((p) => ({
    t: new Date(p.t).getTime(),
    minutes: Math.round(p.totalSeconds / 60),
    sessions: p.sessions,
    uniques: p.uniques,
  }));
  const totalHours = points.reduce((s, p) => s + p.totalSeconds, 0) / 3600;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="inline-flex items-center gap-2">
              <Headphones className="h-4 w-4 text-brand" /> Audio &amp; video play-time
            </CardTitle>
            <CardDescription>
              Total foreground playback per bucket — aggregate engagement across live radio,
              episodes, and YouTube. Total in range: {totalHours.toFixed(1)} hour(s) of playback.
            </CardDescription>
          </div>
          <Select value={scopeType} onValueChange={(v) => setScopeType(v as typeof scopeType)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All scopes</SelectItem>
              <SelectItem value="channel">Live radio</SelectItem>
              <SelectItem value="episode">Episodes</SelectItem>
              <SelectItem value="youtube">YouTube</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {history.isLoading ? (
          <Spinner label="Loading…" />
        ) : data.length === 0 ? (
          <Empty
            title="No playback in this range"
            description="Mobile clients haven't reported any `play_session` events for the selected scope."
          />
        ) : (
          <PlayTimeChart history={history.data!} data={data} />
        )}
      </CardContent>
    </Card>
  );
}

function PlayTimeChart({
  history,
  data,
}: {
  history: PlayTimeHistory;
  data: { t: number; minutes: number; sessions: number; uniques: number }[];
}): JSX.Element {
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 8 }} barCategoryGap="20%">
          <CartesianGrid stroke={CHART_COLORS.line} strokeOpacity={0.5} strokeDasharray="2 4" />
          {/* Category axis — see views-bar-chart comment for the rationale. */}
          <XAxis
            dataKey="t"
            type="category"
            tickFormatter={(v) => xAxisFmt(Number(v), history.bucket)}
            stroke={CHART_COLORS.inkSubtle}
            fontSize={11}
            padding={{ left: 16, right: 16 }}
          />
          <YAxis
            stroke={CHART_COLORS.inkSubtle}
            fontSize={11}
            allowDecimals={false}
            tickFormatter={(v) => `${Number(v).toLocaleString()}m`}
          />
          <Tooltip
            formatter={(v: unknown, name) =>
              name === 'Played'
                ? [`${Number(v).toLocaleString()} min`, name]
                : [Number(v).toLocaleString(), name]
            }
            labelFormatter={(v) => fmtDateTime(new Date(v).toISOString())}
            contentStyle={{
              background: CHART_COLORS.surface,
              border: `1px solid ${CHART_COLORS.border}`,
              fontSize: 12,
            }}
          />
          <Bar dataKey="minutes" name="Played" fill={CHART_COLORS.brand} maxBarSize={40} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Helpers ----------

/** Format dwell seconds as "12s" / "3m 12s" / "1h 03m" — short for table cells. */
function formatDwell(secs: number): string {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function xAxisFmt(ms: number, bucket: 'minute' | 'hour' | 'day'): string {
  const d = new Date(ms);
  if (bucket === 'day') return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// ---------- Reach (geo heatmap) ----------

function ReachPanel(): JSX.Element {
  const [days, setDays] = useState<7 | 30 | 90>(7);
  const [localeFilter, setLocaleFilter] = useState<string | undefined>(undefined);
  const available = useLocaleStore((s) => s.available);
  const reach = useGeoReach(days, localeFilter);

  const points = reach.data?.points ?? [];
  const totalSessions = points.reduce((sum, p) => sum + p.sessions, 0);
  const totalUniques = points.reduce((sum, p) => sum + p.uniques, 0);
  const cityCount = points.length;

  return (
    <Card>
      <CompactHeader
        icon={<Globe className="h-4 w-4 text-brand" />}
        title="Reader reach"
        hint="City-level map from session IPs. Anonymous, no GPS. Brighter = more unique devices."
        action={
          <div className="flex items-center gap-2">
            <RangeButtons value={days} onChange={setDays} />
            <select
              value={localeFilter ?? ''}
              onChange={(e) => setLocaleFilter(e.target.value || undefined)}
              className="rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink-muted hover:bg-surface-muted"
            >
              <option value="">All locales</option>
              {available.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.code.toUpperCase()} · {l.label}
                </option>
              ))}
            </select>
          </div>
        }
      />
      <CardContent>
        <div className="mb-3 grid grid-cols-3 gap-3 text-xs text-ink-muted">
          <div>
            <span className="block font-mono uppercase tracking-wider text-ink-subtle">Cities</span>
            <span className="text-base font-semibold text-ink">
              {reach.isLoading ? '—' : cityCount.toLocaleString()}
            </span>
          </div>
          <div>
            <span className="block font-mono uppercase tracking-wider text-ink-subtle">Sessions</span>
            <span className="text-base font-semibold text-ink">
              {reach.isLoading ? '—' : totalSessions.toLocaleString()}
            </span>
          </div>
          <div>
            <span className="block font-mono uppercase tracking-wider text-ink-subtle">Devices</span>
            <span className="text-base font-semibold text-ink">
              {reach.isLoading ? '—' : totalUniques.toLocaleString()}
            </span>
          </div>
        </div>
        {reach.isLoading ? (
          <div className="flex h-[420px] items-center justify-center">
            <Spinner label="Loading reach data…" />
          </div>
        ) : points.length === 0 ? (
          <Empty
            title="No reach data yet"
            description="Sessions stamped with geo location will appear here as soon as the mobile apps with the geo-aware build start checking in."
          />
        ) : (
          <>
            <GeoHeatmap points={points} className="h-[480px] w-full" />
            <div className="mt-6">
              <h4 className="mb-2 inline-flex items-center gap-2 text-[10.5px] font-mono font-semibold uppercase tracking-[0.18em] text-ink-subtle">
                <Globe className="h-3 w-3" /> Top countries
              </h4>
              <TopCountriesTable points={points} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Sessions hour-of-day heatmap (Audience tab) ----------

const INSIGHTS_RANGES: Array<{ key: 7 | 30 | 90; label: string }> = [
  { key: 7, label: '7d' },
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
];

function SessionsHeatmapPanel(): JSX.Element {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const heatmap = useSessionsHeatmap(days);

  return (
    <Card>
      <CompactHeader
        icon={<Clock className="h-4 w-4 text-brand" />}
        title="When are readers active?"
        hint="Unique devices by Lagos hour × day. Times your daily publish, pushes, and brief refreshes."
        action={<RangeButtons value={days} onChange={setDays} />}
      />
      <CardContent>
        {heatmap.isLoading ? (
          <div className="flex h-[220px] items-center justify-center">
            <Spinner label="Loading…" />
          </div>
        ) : !heatmap.data || heatmap.data.cells.length === 0 ? (
          <Empty
            title="No session data yet"
            description="Once mobile-app sessions start landing, the heatmap will fill in within minutes."
          />
        ) : (
          <SessionsHourHeatmap cells={heatmap.data.cells} />
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Push send-time vs CTR (Engagement tab) ----------

function PushSendTimePanel(): JSX.Element {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const data = usePushSendTimeCtr(days);

  return (
    <Card>
      <CompactHeader
        icon={<TrendingUp className="h-4 w-4 text-brand" />}
        title="Best hour to send a push"
        hint="Sends per Lagos-hour with CTR overlay. Picks the auto-scheduler's sweet-spot windows."
        action={<RangeButtons value={days} onChange={setDays} />}
      />
      <CardContent>
        {data.isLoading ? (
          <div className="flex h-[220px] items-center justify-center">
            <Spinner label="Loading…" />
          </div>
        ) : !data.data || data.data.points.length === 0 ? (
          <Empty
            title="No push data in this range"
            description="Send a few breaking-news broadcasts and refresh — by-hour breakdown shows up as soon as deliveries are recorded."
          />
        ) : (
          <PushSendTimeCtr points={data.data.points} />
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Device + OS breakdown (Reach tab) ----------

function DeviceBreakdownPanel(): JSX.Element {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const data = useDeviceBreakdown(days);

  return (
    <Card>
      <CompactHeader
        icon={<Smartphone className="h-4 w-4 text-brand" />}
        title="What devices are people using?"
        hint="OS, version, and model breakdown. Sets QA focus, support-floor, and test-rack priorities."
        action={<RangeButtons value={days} onChange={setDays} />}
      />
      <CardContent>
        {data.isLoading ? (
          <div className="flex h-[220px] items-center justify-center">
            <Spinner label="Loading…" />
          </div>
        ) : !data.data || data.data.byOs.length === 0 ? (
          <Empty
            title="No device data yet"
            description="Sessions from app builds with the device-info payload will populate this within minutes of going live."
          />
        ) : (
          <DeviceBreakdown
            byOs={data.data.byOs}
            byOsVersion={data.data.byOsVersion}
            byDeviceModel={data.data.byDeviceModel}
            totalDevices={data.data.totalDevices}
            devicesMissingDetails={data.data.devicesMissingDetails}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One-line title + helper-text variant of CardHeader. Keeps the full caption
 * (the editorial "what + why") visible without the multi-line vertical bloat
 * the default 4-line CardTitle/CardDescription pair produces. Filters slot
 * into `action` and live to the right of the title at md+; on small screens
 * the action wraps under the heading.
 */
function CompactHeader({
  icon,
  title,
  hint,
  action,
}: {
  icon: JSX.Element;
  title: string;
  hint: string;
  action?: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-line-hair px-5 py-3">
      <div className="min-w-0 flex-1">
        <h3 className="inline-flex items-center gap-2 font-heading text-[14.5px] font-semibold leading-tight tracking-tight text-ink">
          {icon}
          {title}
        </h3>
        <p className="mt-0.5 text-[12px] leading-snug text-ink-muted">{hint}</p>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

// Shared range picker for the new insight panels.
function RangeButtons({
  value,
  onChange,
}: {
  value: 7 | 30 | 90;
  onChange: (v: 7 | 30 | 90) => void;
}): JSX.Element {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-line">
      {INSIGHTS_RANGES.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onChange(r.key)}
          className={
            'px-2.5 py-1 text-xs font-medium transition-colors ' +
            (value === r.key
              ? 'bg-brand text-white'
              : 'bg-surface text-ink-muted hover:bg-surface-muted')
          }
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}
